import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, UserPlus } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { analyticsAPI } from '../../../utils/api';
import { getErrorMessage } from '../../../utils/errorTranslation';

const CandidatesMetrics = ({ filters, showToast }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getCandidatesMetrics({
        period: filters.period,
        offerId: filters.offerId
      });
      setData(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching candidates metrics:', error);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.analytics.error.fetchFailed')), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.offerId, filters.period, showToast, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 h-full min-h-[320px]"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('dashboard.analytics.candidates.title')}
          </h3>
          <p className="text-sm text-gray-500">{t('dashboard.analytics.candidates.subtitle')}</p>
        </div>
        <Users className="h-6 w-6 text-primary-600" />
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : !data ? (
        <div className="text-sm text-gray-500">{t('dashboard.analytics.candidates.noData')}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-gray-500">
                <Users className="h-4 w-4 mr-1" />
                {t('dashboard.analytics.candidates.total')}
              </div>
              <div className="text-lg font-semibold text-gray-900">{data.total || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-gray-500">
                <UserPlus className="h-4 w-4 mr-1" />
                {t('dashboard.analytics.candidates.new')}
              </div>
              <div className="text-lg font-semibold text-gray-900">{data.newApplications || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-gray-500">
                <TrendingUp className="h-4 w-4 mr-1" />
                {t('dashboard.analytics.candidates.avgPerOffer')}
              </div>
              <div className="text-lg font-semibold text-gray-900">{data.avgPerOffer || 0}</div>
            </div>
          </div>

          {data.byStatus && (
            <div className="text-sm text-gray-600">
              <span className="mr-3">{t('dashboard.analytics.candidates.applications')}: </span>
              {Object.entries(data.byStatus).map(([status, count]) => (
                <span key={status} className="mr-3">
                  {status}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default CandidatesMetrics;
